import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
export function TopographicContours() {
  // Generate paths and markers once to avoid jumping on re-renders
  const {
    contours,
    markers
  } = useMemo(() => {
    // Helper to generate a smooth, irregular closed path
    const generateBlobPath = (cx: number, cy: number, baseRadius: number, numPoints: number, variance: number) => {
      const points = [];
      for (let i = 0; i < numPoints; i++) {
        const angle = i / numPoints * Math.PI * 2;
        // Randomize radius within variance
        const r = baseRadius + (Math.random() * 2 - 1) * variance;
        points.push({
          x: cx + Math.cos(angle) * r,
          y: cy + Math.sin(angle) * r
        });
      }
      // Create a smooth closed path using quadratic bezier curves
      let path = `M ${(points[0].x + points[numPoints - 1].x) / 2},${(points[0].y + points[numPoints - 1].y) / 2}`;
      for (let i = 0; i < numPoints; i++) {
        const p1 = points[i];
        const p2 = points[(i + 1) % numPoints];
        const midX = (p1.x + p2.x) / 2;
        const midY = (p1.y + p2.y) / 2;
        path += ` Q ${p1.x},${p1.y} ${midX},${midY}`;
      }
      return path;
    };
    const levels = 8;
    const generatedContours = [];
    const generatedMarkers = [];
    for (let i = 0; i < levels; i++) {
      // Base radius expands outward
      const baseRadius = 80 + i * 120;
      // More points for larger circles to maintain smoothness
      const numPoints = 6 + i * 2;
      // Variance scales with radius to keep the organic feel proportional
      const variance = baseRadius * 0.15;
      const paths = [];
      // Generate 4 distinct shapes to morph between
      for (let j = 0; j < 4; j++) {
        paths.push(generateBlobPath(500, 500, baseRadius, numPoints, variance));
      }
      // Close the loop for seamless animation
      paths.push(paths[0]);
      generatedContours.push({
        paths,
        // Inner contours move faster (12s), outer slower (26s)
        duration: 12 + i * 2,
        // Inner contours slightly more opaque (15% -> 8%)
        opacity: 0.15 - i * 0.01,
        // Inner contours slightly thinner
        strokeWidth: 1 + i * 0.05
      });
      // Add 2-3 elevation markers near this contour level
      const numMarkers = 2 + Math.floor(Math.random() * 2);
      for (let m = 0; m < numMarkers; m++) {
        const angle = Math.random() * Math.PI * 2;
        const r = baseRadius + (Math.random() * 2 - 1) * (variance * 0.8);
        generatedMarkers.push({
          cx: 500 + Math.cos(angle) * r,
          cy: 500 + Math.sin(angle) * r,
          delay: Math.random() * 5,
          duration: 3 + Math.random() * 3
        });
      }
    }
    return {
      contours: generatedContours,
      markers: generatedMarkers
    };
  }, []);
  return <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-247" data-mp="3uv1d7yho1oiq">
      <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" viewBox="0 0 1000 1000" data-id="element-248" data-mp="3lchzhe5bkv1">
        <defs data-id="element-249" data-mp="8ssmbkc0b70c">
          <filter id="glow-marker" data-id="element-250" data-mp="8s0zaxwh6hlo">
            <feGaussianBlur stdDeviation="2" result="coloredBlur" data-id="element-251" data-mp="40rdy9hym0fl" />
            <feMerge data-id="element-252" data-mp="3yy82wf67k8n">
              <feMergeNode in="coloredBlur" data-id="element-253" data-mp="3yeqjgtfmxv5" />
              <feMergeNode in="SourceGraphic" data-id="element-254" data-mp="3w3ill6brwo8q" />
            </feMerge>
          </filter>
        </defs>

        {/* Animated Topographic Contours */}
        {contours.map((contour, i) => <motion.path key={`contour-${i}`} animate={{
        d: contour.paths
      }} transition={{
        duration: contour.duration,
        repeat: Infinity,
        ease: 'easeInOut'
      }} fill="none" stroke="#78e1c4" strokeWidth={contour.strokeWidth} strokeOpacity={contour.opacity} strokeLinecap="round" strokeLinejoin="round" data-id="element-255" data-mp="3m64cjrs6j1qg" data-mpi={i} />)}

        {/* Elevation Markers */}
        {markers.map((marker, i) => <g key={`marker-${i}`} data-id="element-256" data-mp="1nh5kb87pjnb8" data-mpi={i}>
            {/* Pulsing halo */}
            <motion.circle cx={marker.cx} cy={marker.cy} r="4" fill="#78e1c4" filter="url(#glow-marker)" animate={{
          opacity: [0, 0.5, 0],
          scale: [1, 2.5, 1]
        }} transition={{
          duration: marker.duration,
          repeat: Infinity,
          delay: marker.delay,
          ease: 'easeInOut'
        }} data-id="element-257" data-mp="1ne9mdjvv0yms" />
            {/* Core dot */}
            <circle cx={marker.cx} cy={marker.cy} r="1.5" fill="#78e1c4" opacity="0.7" data-id="element-258" data-mp="urot2dvtqcgl" />
          </g>)}
      </svg>
    </div>;
}